import { useEffect, useRef, useState } from 'preact/hooks';
import { FACTIONS, PERSONAS, type Faction, type Persona } from '@aurane/protocol';
import { connect, createGuest, fetchCapitals, fetchPublicConfig, foundColony, loginWithPasskey, passkeysSupported, redeem, startEmail, startEmailLogin, verifyEmailLogin, type FoundOffer, type SignInResult } from '../net.js';
import { lang, setLang, t, tError } from '../i18n/index.js';
import type { CapitalOffer } from '@aurane/sim';
import { useSig } from './useSig.js';
import { InstallButton } from './bits.js';
import { Prologue, prologueSeen } from './Prologue.js';

export function Landing() {
  const [name, setName] = useState('');
  const [faction, setFaction] = useState<Faction>('guild');
  const [persona, setPersona] = useState<Persona>('oriel');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState('');
  // Asked at the founding (decision Nick, 30.09): the address that identifies the Colony and finds it back. Optional;
  // a six-digit code confirms it once in the game.
  const [foundEmail, setFoundEmail] = useState('');
  // A refused code is said next to its field too, and the field takes the focus (calibration playtest 29.09: the error
  // under the button went unseen, « the button looks dead »).
  const [inviteError, setInviteError] = useState<string | null>(null);
  const inviteRef = useRef<HTMLInputElement>(null);
  const [requireInvite, setRequireInvite] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [mail, setMail] = useState<'off' | 'address' | 'code'>('off');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [handle, setHandle] = useState<string | null>(null);
  /** A returning account with no colony this season: the founding form, prefilled, no invitation. */
  const [returning, setReturning] = useState<FoundOffer | null>(null);
  const l = useSig(lang);
  useEffect(() => { void fetchPublicConfig().then((c) => setRequireInvite(c.requireInvite)); }, []);
  // The first visit starts with the language, in both (issue #47): nothing else is readable before it.
  const [langChosen, setLangChosen] = useState(() => { try { return !!localStorage.getItem('aurane.lang'); } catch { return true; } });
  // The Awakening is told once, after the language and before the form (playtest 09.10); a returning player never sees it.
  const [showPrologue, setShowPrologue] = useState(() => !prologueSeen());
  // The founding choice (issue #47): three capitals, the General recommends one in its temperament.
  const [offer, setOffer] = useState<CapitalOffer | null>(null);
  const [capital, setCapital] = useState<string | null>(null);
  useEffect(() => { let live = true; void fetchCapitals(faction, persona).then((o) => { if (!live) return; setOffer(o); setCapital(o?.choices[o.pick]?.system ?? null); }); return () => { live = false; }; }, [faction, persona]);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true); setError(null); setInviteError(null);
    try {
      if (returning) await foundColony(returning.ticket, name.trim(), faction, persona, capital ?? undefined);
      else {
        const made = await createGuest(name.trim(), faction, persona, invite.trim() || undefined, capital ?? undefined);
        if (made.resumed) { try { sessionStorage.setItem('aurane.resumed', made.name ?? ''); } catch { /* ignore */ } }
        else if (foundEmail.trim()) {
          const sent = await startEmail(foundEmail.trim(), lang.value);
          // Said in the game, never swallowed (#77: an address already protecting another Colony vanished silently).
          try { if (sent.ok) localStorage.setItem('aurane.pendingEmail', foundEmail.trim()); else sessionStorage.setItem('aurane.emailRefused', JSON.stringify({ email: foundEmail.trim(), reason: sent.reason ?? '' })); } catch { /* ignore */ }
        }
      }
      connect();
    } catch (err) {
      const reason = (err as Error).message;
      if (reason === 'ticket expired or used') setReturning(null);
      setError(tError(reason));
      if (reason === 'invalid invite' || reason === 'invite required') { setInviteError(tError(reason)); inviteRef.current?.focus(); inviteRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    } finally { setBusy(false); }
  };
  /** After a sign-in: the colony opens, or a returning account gets the founding form. */
  const signedIn = (r: SignInResult, failed: (reason: string) => string) => {
    if (r.ok) { connect(); return; }
    if (r.found) {
      setReturning(r.found);
      if (r.found.previous) { setName(r.found.previous.name); setFaction(r.found.previous.faction); setPersona(r.found.previous.persona); }
      setMail('off'); setJoining(false);
      return;
    }
    setError(r.reason === 'no colony this season' ? t('noColonyThisSeason') : failed(r.reason ?? ''));
  };
  const signIn = async () => {
    setBusy(true); setError(null);
    const r = await loginWithPasskey();
    setBusy(false);
    if (r.ok || r.reason !== 'cancelled') signedIn(r, (reason) => t('signInFailed').replace('{r}', tError(reason)));
  };
  const askCode = async (e: Event) => {
    e.preventDefault(); setBusy(true); setError(null);
    const h = await startEmailLogin(email.trim(), l);
    setBusy(false);
    if (!h) { setError(t('signInFailed').replace('{r}', '')); return; }
    setHandle(h); setCode(''); setMail('code');
  };
  const useCode = async (e: Event) => {
    e.preventDefault(); if (!handle) return;
    setBusy(true); setError(null);
    const r = await verifyEmailLogin(handle, code);
    setBusy(false);
    signedIn(r, tError);
  };
  const join = async (e: Event) => {
    e.preventDefault();
    const code = joinCode.trim().replace(/^.*#join=/, '');
    if (!code) return;
    setBusy(true); setError(null);
    try { await redeem(decodeURIComponent(code)); connect(); } catch (err) { setError(tError((err as Error).message)); } finally { setBusy(false); }
  };

  if (!langChosen) {
    return (
      <div class="landing">
        <img src="/icon.svg" alt="" class="logo" />
        <h1>Aurane</h1>
        <div class="card langpick">
          <p>Choisis ta langue · Choose your language</p>
          <div class="actions">
            <button class="primary" onClick={() => { setLang('fr'); setLangChosen(true); }}>Français</button>
            <button class="primary" onClick={() => { setLang('en'); setLangChosen(true); }}>English</button>
          </div>
        </div>
      </div>
    );
  }
  if (showPrologue) return <Prologue onDone={() => setShowPrologue(false)} />;
  const pick = offer?.choices[offer.pick];
  return (
    <div class="landing">
      <div class="lang"><button class={l === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button><button class={l === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button></div>
      <img src="/icon.svg" alt="" class="logo" />
      <h1>Aurane</h1>
      <p class="tagline">{t('tagline')}</p>
      <p class="subtitle">{t('subtitle')}</p>
      <form onSubmit={submit} class="card">
        {returning && (
          <div class="returning">
            <b>{t('returningTitle')}</b>
            <p>{returning.remembers ? t('returningRemembers') : t('returningFresh')}</p>
          </div>
        )}
        <label>{t('yourName')}<input value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} minLength={2} maxLength={32} required autoFocus /></label>
        {requireInvite && !returning && <label>{t('inviteCode')}<input ref={inviteRef} value={invite} aria-invalid={inviteError ? 'true' : undefined} onInput={(e) => { setInvite((e.target as HTMLInputElement).value); setInviteError(null); }} placeholder="AUR-XXXXXXXX" maxLength={40} required />{inviteError && <span class="error small">{inviteError}</span>}</label>}
        {!returning && <label>{t('foundEmail')}<input type="email" inputMode="email" autoComplete="email" value={foundEmail} onInput={(e) => setFoundEmail((e.target as HTMLInputElement).value)} placeholder={t('foundEmailHint')} maxLength={254} /></label>}
        <fieldset>
          <legend>{t('faction')}</legend>
          <div class="choices">
            {FACTIONS.map((f) => (
              <button type="button" key={f} class={`choice f-${f} ${faction === f ? 'on' : ''}`} onClick={() => setFaction(f)}>
                <b>{t(f)}</b><small>{t(`${f}Desc`)}</small>
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>{t('general')}</legend>
          <p class="muted small hint">{t('generalHint')}</p>
          <div class="choices">
            {PERSONAS.map((p) => (
              <button type="button" key={p} class={`choice ${persona === p ? 'on' : ''}`} onClick={() => setPersona(p)}>
                <b>{t(p)}</b><small>{t(`${p}Desc`)}</small><small class="hello">{t(`${p}Hello`)}</small>
              </button>
            ))}
          </div>
        </fieldset>
        {offer && offer.choices.length > 0 && (
          <fieldset class="capitals">
            <legend>{t('capitalTitle')}</legend>
            {pick && <p class="advice"><b>{t(persona)}</b> : {t(`capWhy_${offer.why}` as 'capWhy_far').replace('{s}', pick.name).replace('{n}', String(pick.neighbour ?? '')).replace('{k}', String(pick.linkable))}</p>}
            <div class="choices">
              {offer.choices.map((c, i) => (
                <button type="button" key={c.system} class={`choice ${capital === c.system ? 'on' : ''}`} onClick={() => setCapital(c.system)}>
                  <b>{c.name}{i === offer.pick ? ' ★' : ''}</b>
                  <small>{t(c.resource)} · {t('band')} {c.band}{c.kind === 'pulsar' ? ` · ${t('kindPulsar')}` : ''}</small>
                  <small>{t('capitalLinkable').replace('{n}', String(c.linkable))}</small>
                  <small>{c.neighbour === null ? t('capitalAlone') : t('capitalNeighbour').replace('{n}', String(c.neighbour))}</small>
                  {i === offer.pick && <small class="rec">{t('capitalRecommended').replace('{g}', t(persona))}</small>}
                </button>
              ))}
            </div>
          </fieldset>
        )}
        {error && <p class="error">{error}</p>}
        <button class="primary" disabled={busy || name.trim().length < 2 || (requireInvite && !returning && invite.trim().length < 4)}>{returning ? t('returningFound') : t('play')}</button>
        {returning && <p class="muted small"><button type="button" class="link" onClick={() => { setReturning(null); setName(''); setError(null); }}>{t('returningOther')}</button></p>}
        {!returning && <>
        {passkeysSupported() && <button type="button" class="passkey" disabled={busy} onClick={() => void signIn()}>{t('signInPasskey')}</button>}
        {mail === 'off' ? <button type="button" class="passkey" disabled={busy} onClick={() => { setMail('address'); setError(null); }}>{t('signInEmail')}</button> : (
          <div class="join emailform">
            {mail === 'address' ? (
              <>
                <p class="muted small">{t('signInEmailHelp')}</p>
                <label>{t('emailAddress')}<input type="email" inputMode="email" autoComplete="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} autoFocus /></label>
                <div class="actions"><button type="button" disabled={busy || !email.includes('@')} onClick={(e) => void askCode(e)}>{t('sendCode')}</button><button type="button" onClick={() => setMail('off')}>{t('cancel')}</button></div>
              </>
            ) : (
              <>
                <p class="muted small">{t('signInEmailSent')}</p>
                <label>{t('typeCode')}<input class="code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} autoFocus /></label>
                <div class="actions"><button type="button" class="primary" disabled={busy || code.replace(/\D/g, '').length !== 6} onClick={(e) => void useCode(e)}>{t('openColony')}</button><button type="button" onClick={() => setMail('address')}>{t('back')}</button></div>
              </>
            )}
          </div>
        )}
        <p class="muted small"><button type="button" class="link" onClick={() => setJoining(!joining)}>{t('haveColony')}</button></p>
        <p class="muted small"><button type="button" class="link" onClick={() => setShowPrologue(true)}>{t('prologueReplay')}</button></p>
        <InstallButton compact />
        {joining && (
          <div class="join">
            <label>{t('pasteLink')}<input value={joinCode} onInput={(e) => setJoinCode((e.target as HTMLInputElement).value)} placeholder="https://play.playaurane.com/#join=…" /></label>
            <button type="button" disabled={busy || !joinCode.trim()} onClick={(e) => void join(e)}>{t('openColony')}</button>
          </div>
        )}
        </>}
      </form>
    </div>
  );
}
